import {
  BlockOutlined,
  DisconnectOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { type TreeDropEvent } from '@framework/ui/components/tree/draggableTreeTypes.ts';
import { applyDropToTree } from '@framework/ui/components/tree/draggableTreeUtils.ts';
import { Button, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { type IMapGroup } from '../../layers/MapGroup.ts';
import { type IMapLayer } from '../../layers/MapLayer.ts';
import { confirmAndDeleteGroup } from '../confirmAndDeleteGroup.ts';
import {
  buildConfigTreeData,
  flattenTreeToLayers,
  type IConfigTreeNode,
} from './buildConfigTreeData.ts';

interface IGroupDropOptions {
  info: TreeDropEvent;
  groupEditor: RelationEditor<IMapGroup>;
}

interface ILayerDropOptions {
  info: TreeDropEvent;
  treeData: IConfigTreeNode[];
  layersEditor: RelationEditor<IMapLayer>;
}

/**
 * Gestisce il drop di un layer nell'albero.
 * Applica la nuova posizione e aggiorna il campo `group`
 * tramite `flattenTreeToLayers`.
 */
function handleLayerDrop({ info, treeData, layersEditor }: ILayerDropOptions) {
  // Applica il drop all'albero generico (sposta il nodo nella nuova posizione)
  const newTree = applyDropToTree({
    treeData,
    dragKey: info.dragNode.key as string,
    dropKey: info.node.key as string,
    dropToGap: info.dropToGap,
    dropPosition: info.dropPosition,
  });
  if (!newTree) return;

  // Ricostruisce l'array piatto di layer con il campo `group` aggiornato
  const newLayers = flattenTreeToLayers(newTree);
  layersEditor.replace(newLayers);
}

/**
 * Gestisce il drop di un gruppo nell'albero.
 * Riordina l'array e ricalcola il campo `order` di ogni gruppo
 * in base alla nuova posizione.
 */
function handleGroupDrop({ info, groupEditor }: IGroupDropOptions) {
  const dragKey = info.dragNode.key as string;
  const dropKey = info.node.key as string;

  // I gruppi possono essere spostati solo tra altri gruppi
  if (!dropKey.startsWith('group::')) return;

  // Estrai i nomi dei gruppi dalle chiavi (formato "group::{name}")
  const dragName = dragKey.replace('group::', '');
  const dropName = dropKey.replace('group::', '');
  const items = groupEditor.items;
  const oldIndex = items.findIndex(group => group.name === dragName);
  const dropIndex = items.findIndex(group => group.name === dropName);
  if (oldIndex === -1 || dropIndex === -1) return;

  if (oldIndex !== dropIndex) {
    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);

    // Trova la posizione del target dopo la rimozione e inserisci
    // prima o dopo in base a dropPosition (-1 = prima, 1 = dopo)
    const dropIndexAfterRemoval = reordered.findIndex(
      group => group.name === dropName,
    );
    if (dropIndexAfterRemoval === -1) return;

    const insertIndex =
      info.dropPosition === -1
        ? dropIndexAfterRemoval
        : dropIndexAfterRemoval + 1;
    reordered.splice(insertIndex, 0, moved);

    // Ricalcola il campo `order` (usato a runtime da buildGroupedLayers)
    const updated = reordered.map((group, index) => ({
      ...group,
      order: index,
    }));
    groupEditor.replace(updated);
  }
}

export interface IUseMapTreeConfigOptions {
  groupEditor: RelationEditor<IMapGroup>;
  layersEditor: RelationEditor<IMapLayer>;
}

/**
 * Hook che racchiude tutta la logica dell'albero di configurazione
 * gruppi/layer del widget mappa: costruzione dati, drop, azioni
 * (modifica, elimina, sgancia dal gruppo) e icone per nodo.
 */
export function useMapTreeConfig(options: IUseMapTreeConfigOptions) {
  const { groupEditor, layersEditor } = options;

  // Costruisce l'albero da gruppi e layer per il componente DraggableTree
  const treeData = useMemo(
    () => buildConfigTreeData(groupEditor.items, layersEditor.items),
    [groupEditor.items, layersEditor.items],
  );

  // Chiavi dei gruppi attualmente presenti nell'albero.
  const groupKeys = useMemo(
    () => groupEditor.items.map(group => `group::${group.name}`),
    [groupEditor.items],
  );

  // I gruppi sono espansi di default: memorizziamo solo quelli che l'utente
  // ha esplicitamente chiuso. `expandedKeys` è quindi derivato durante il
  // render, e un gruppo creato dopo il primo render risulta espanso senza
  // bisogno di sincronizzare lo stato.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const expandedKeys = useMemo(
    () => groupKeys.filter(key => !collapsedKeys.has(key)),
    [groupKeys, collapsedKeys],
  );

  const onExpandedKeysChange = useCallback(
    (keys: string[]) => {
      const expandedSet = new Set(keys);
      setCollapsedKeys(new Set(groupKeys.filter(key => !expandedSet.has(key))));
    },
    [groupKeys],
  );

  // Contesto di validazione per lo schema del gruppo (nomi univoci)
  // Esclude il nome del gruppo attualmente in modifica
  const validationContext = useMemo(
    () => ({
      existingGroupNames: groupEditor.items
        .map(group => group.name)
        .filter(name => name !== groupEditor.handle.current?.name),
    }),
    [groupEditor.items, groupEditor.handle.current?.name],
  );

  // Elimina un gruppo. Se ha layer associati, chiede conferma e
  // li sposta nella sezione "Altri" (group: undefined)
  const handleDeleteGroup = useCallback(
    (groupIndex: number) => {
      confirmAndDeleteGroup(groupEditor, layersEditor, groupIndex);
    },
    [groupEditor, layersEditor],
  );

  // Dispatcher del drop: distingue gruppi da layer in base alla chiave
  const handleDrop = useCallback(
    (info: TreeDropEvent) => {
      const dragKey = info.dragNode.key as string;
      if (dragKey.startsWith('group::')) {
        handleGroupDrop({ info, groupEditor });
      } else {
        handleLayerDrop({ info, treeData, layersEditor });
      }
    },
    [treeData, groupEditor, layersEditor],
  );

  // Apre il drawer di modifica per il nodo (gruppo o layer)
  const handleEdit = useCallback(
    (node: IConfigTreeNode) => {
      if (node.data?.nodeType === 'group' && node.data.groupData) {
        groupEditor.handle.open(node.data.groupData);
      } else if (node.data?.layerData) {
        layersEditor.handle.open(node.data.layerData);
      }
    },
    [groupEditor, layersEditor],
  );

  // Elimina il nodo: per i gruppi chiede conferma, per i layer rimuove direttamente
  const handleDelete = useCallback(
    (node: IConfigTreeNode) => {
      if (node.data?.nodeType === 'group' && node.data.groupIndex != null) {
        handleDeleteGroup(node.data.groupIndex);
      } else if (node.data?.layerIndex != null) {
        layersEditor.removeAt(node.data.layerIndex);
      }
    },
    [layersEditor, handleDeleteGroup],
  );

  // Icona diversa per tipo di nodo: cartella per gruppi, blocco per layer
  const nodeIcon = useCallback((node: IConfigTreeNode) => {
    return node.data?.nodeType === 'group' ? (
      <FolderOutlined />
    ) : (
      <BlockOutlined />
    );
  }, []);

  /**
   * Regole di drop:
   * - Layer → gruppo: solo inside (dropPosition 0)
   * - Gruppo → gruppo: solo gap (dropPosition !== 0), per riordinamento
   * - Gap tra tipi diversi (gruppo↔layer): rifiutato
   * - Qualsiasi → foglia: rifiutato
   */
  const allowDrop = useCallback(
    ({
      dragNode,
      dropNode,
      dropPosition,
    }: {
      dragNode: { key: React.Key };
      dropNode: { key: React.Key; isLeaf?: boolean };
      dropPosition: -1 | 0 | 1;
    }) => {
      const dragKey = String(dragNode.key);
      const dropKey = String(dropNode.key);
      const isDragGroup = dragKey.startsWith('group::');
      const isDropGroup = dropKey.startsWith('group::');

      if (dropPosition === 0) {
        if (dropNode.isLeaf) return false;
        // Solo layer possono entrare in un gruppo
        return isDropGroup && !isDragGroup;
      }

      // Gap: consenti solo spostamenti tra nodi dello stesso tipo
      if (isDragGroup !== isDropGroup) return false;
      return true;
    },
    [],
  );

  // Bottone "Sgancia dal gruppo" visibile solo per layer dentro un gruppo.
  // Imposta group: undefined e sposta il layer nella sezione "Altri".
  const nodeExtraActions = useCallback(
    (node: IConfigTreeNode) => {
      if (node.data?.nodeType !== 'layer' || !node.data.layerData?.group) {
        return undefined;
      }

      return (
        <Tooltip title="Sgancia dal gruppo">
          <Button
            size="small"
            type="text"
            icon={<DisconnectOutlined />}
            onClick={event => {
              event.stopPropagation();
              if (node.data?.layerIndex != null) {
                layersEditor.updateAt(node.data.layerIndex, {
                  ...node.data.layerData!,
                  group: undefined,
                });
              }
            }}
          />
        </Tooltip>
      );
    },
    [layersEditor],
  );

  return {
    treeData,
    expandedKeys,
    onExpandedKeysChange,
    validationContext,
    handleDrop,
    handleEdit,
    handleDelete,
    nodeIcon,
    nodeExtraActions,
    allowDrop,
  };
}
